import authHeader from "./auth-header";
import router from "@/router";
import { useAuthenticationStore } from "@/stores/authentication";
import { useAlertsStore } from "@/stores/alerts";


// const API_URL = 'http://127.0.0.1:8000/api/v1';
// const AUTH_URL= 'http://127.0.0.1:8000/api/auth';
// const SSO_URL = 'http://127.0.0.1:8000/api/sso';

const BASE_URL = import.meta.env.VITE_BASE_URL;
const API_URL = BASE_URL + "/api/v1";
const AUTH_URL = BASE_URL + "/api/auth";
const SSO_URL = BASE_URL + "/api/sso";

export const FetchService = {
  loading: false,
  error: null as any,
  getOptions(fetchMethod, postData) {
    if (postData) {
      return {
        method: fetchMethod.toUpperCase(),
        headers: authHeader(postData),
        body: postData instanceof FormData ? postData : JSON.stringify(postData)
      };
    } else {
      return {
        method: fetchMethod.toUpperCase(),
        headers: authHeader(postData)
      };
    }
  },

  getUrl(endpoint: string) {
    if (/^http(s)?:\/\//.test(endpoint)) {
      return endpoint;
    }
    if (endpoint.startsWith("*")) {
      return BASE_URL + "/api" + endpoint.replace("*", "");
    } else if (endpoint == "/me" || endpoint == "/reset") {
      return AUTH_URL + endpoint;
    } else if (endpoint.indexOf("google/url") >= 0) {
      return SSO_URL + endpoint;
    } else if (endpoint.indexOf("nycdoe/url") >= 0){
      return SSO_URL + endpoint;
    } else return API_URL + endpoint;
  },

  async fetchRaw(endpoint: string) {
    if (router.currentRoute.value.meta.requiresAuth && !useAuthenticationStore().isLoggedIn()) {
      const authStore = useAuthenticationStore();
      const role = (router.currentRoute.value.meta.level as any) || authStore.level;
      const account = (router.currentRoute.value.params?.account as string) || authStore.account;
      if (role === "support") {
        await router.push({ name: "support-login" });
      } else if (account) {
        await router.push({ name: `${role}-login`, params: { account } });
      } else {
        await router.push({ name: `${role}-login` });
      }
      return false;
    }
    this.loading = true;
    const url = this.getUrl(endpoint);
    return fetch(url).then((res) => {
      // a non-200 response code
      if (!res.ok) {
        // create error instance with HTTP status text
        // const error = new Error(res.statusText);
      } else {
        return res.text();
      }
    });
  },
  async fetchData(
    endpoint,
    fetchMethod,
    postData: any = null,
    withRes = false,
    controller?: AbortController
  ) {
    if (router.currentRoute.value.meta.requiresAuth && !useAuthenticationStore().isLoggedIn()) {
      const authStore = useAuthenticationStore();
      const role = (router.currentRoute.value.meta.level as any) || authStore.level;
      const account = (router.currentRoute.value.params?.account as string) || authStore.account;
      if (role === "support") {
        await router.push({ name: "support-login" });
      } else if (account) {
        await router.push({ name: `${role}-login`, params: { account } });
      } else {
        await router.push({ name: `${role}-login` });
      }
      return false;
    }
    this.loading = true;
    const url = this.getUrl(endpoint);
    const options: any = this.getOptions(fetchMethod, postData);

    return fetch(url, { ...options, signal: controller?.signal })
      .then((res) => {
        // a non-200 response code
        if (!res.ok) {
          // create error instance with HTTP status text
          // const error = new Error(res.statusText);
          
          if (res.status == 401 ) {
            console.log("Invalid token");
            const authStore = useAuthenticationStore();
            authStore.setJWT(null);
            router.push({ name: authStore.level + "-login" });
            return false;
            // }else if (res.status == 404){
            //   console.log("Resource not available");
            //   router.push({path: "/error404"});
            //   return false;
          } else if (res.status == 403) {
            const alertStore = useAlertsStore();
            alertStore.addAlert("error", "You don't have permission to perform this operation.");
            return this.parseResponse(res, withRes);
          } else if (res.status == 423) {
            const alertStore = useAlertsStore();
            alertStore.addAlert(
              "error",
              "The resource is locked! Contact support for more details"
            );
            return this.parseResponse(res, withRes);
          } else if (res.status == 409) {
            console.log("409 detected");
            return this.parseResponse(res, withRes);
          } else if (res.status == 500) {
            return {
              status: 500,
              message:
                "There was an internal server error.  The matter is being investigated.  Please try your request again.",
              res
            };
          } else {
            // error.json = res.json();
            return this.parseResponse(res, withRes);
          }
        } else {
          this.loading = false;
          return this.parseResponse(res, withRes);
        }
      })
      .catch((err) => {
        this.loading = false;
        this.error = err;
        // In case a custom JSON error response was provided
        if (err.json) {
          console.log(err);
          return err.json.then((json) => {
            // set the JSON response message
            this.error.message = json.message;
            console.log(this.error.message);
          });
        }
      });
  },
  // async parseResponse(res, withRes = false) {
  //   let data;
  //   if (res.headers["Content-Type"] === "application/json") {
  //     data = await res.json();
  //   } else data = await res.text();
  //   try {
  //     data = JSON.parse(data);
  //   } catch (error) {
  //     //
  //   }
  //   if (withRes) return { data, res };
  //   return data;
  // }

  async parseResponse(res, withRes = false) {
    let data;
    try {
      // Check if response is JSON
      const contentType = res.headers["Content-Type"] || res.headers["content-type"];
      if (contentType && contentType.includes("application/json")) {
        try {
          data = await res.json(); // Parse JSON directly
        } catch (jsonError) {
          console.warn("Failed to parse JSON response:", jsonError);
          // If JSON parsing fails, fall back to text
          const text = await res.text();
          data = text.trim() ? text : null; // Return null for empty responses
        }
      } else {
        data = await res.text(); // Handle plain text or other content types
      }
    } catch (readError) {
      console.error("Error reading response body:", readError);
      data = null; // Return null instead of throwing
    }
  
    // Handle additional JSON parsing only if needed
    if (typeof data === "string" && data.trim()) {
      try {
        data = JSON.parse(data);
      } catch (parseError) {
        console.warn("Response is not valid JSON:", parseError);
      }
    }
  
    // Return response data
    if (withRes) {
      return { data, res };
    }
    return data;
  }
};
export default FetchService;
